import { createInstance } from 'i18next';

import { report, REPORT_TYPES } from '@adsk/exo-skeleton-api';

import { EXO_SKELETON_SCOPE_NAME } from '../consts';
import { supportedLocales, namespaces } from '../locale/consts';

const i18nInstance = createInstance();

const defaultLanguage = 'en';

// Normalization dictionary for more complicated languages / cultures combinations
const normalizeLangDict = {
  // simplified Chinese
  zh: 'zh-Hans',
  'zh-CN': 'zh-Hans',
  'zh-TW': 'zh-Hant',
  'zh-HK': 'zh-Hant-HK',
} as Record<string, string>;

const getNormalizeLang = (lang: string): string => {
  return normalizeLangDict[lang] || lang;
};

/**
 * Find similar languages (pt-BR -> pt)
 * @param lang
 * @return {string}
 */
const getSimilarLang = (lang: string): string => {
  // Chinese have a special logic in `normalizeLangDict`
  if (lang.startsWith('zh')) {
    return lang;
  }
  return lang.substring(0, lang.indexOf('-'));
};

const getBrowserLanguage = (): string => {
  // Safari 9.1.1 has no support for navigator.languages, we only support latest version

  const languages = navigator.languages || [
    navigator.language || defaultLanguage,
  ];
  const userLanguages = [...languages];

  for (const lang of userLanguages) {
    const normalizeLang = getNormalizeLang(lang);
    if (supportedLocales.includes(normalizeLang)) {
      return normalizeLang;
    }
    const similarLang = getSimilarLang(lang);
    if (supportedLocales.includes(similarLang)) {
      return similarLang;
    }
  }

  return defaultLanguage;
};

/**
 * Currently translation files are pretty small, so adding all here eagerly doesn't look too bad.
 * In the future we can lazy load resources on the go if we see a need for it.
 */

type Resources = {
  [lang: string]: {
    [namespace: string]: string;
  };
};

const eagerLoadAllTranslations = (): Resources => {
  const resources: Resources = {};

  supportedLocales.forEach((lang: string) => {
    namespaces.forEach((namespace: string) => {
      resources[lang] = resources[lang] || {};
      resources[lang][namespace] = require(
        `../locale/${lang}/${namespace}.json`,
      );
    });
  });

  return resources;
};

export const initi18n = async () => {
  let language;
  try {
    language = getBrowserLanguage();

    await i18nInstance.init({
      fallbackLng: defaultLanguage,
      lng: language,
      resources: eagerLoadAllTranslations(),
    });
  } catch (err) {
    const errorMessage = 'Failed to initialize i18next';

    void report({
      scope: EXO_SKELETON_SCOPE_NAME,
      type: REPORT_TYPES.error,
      value: err as Error | string,
      context: {
        language,
      },
    });
    throw new Error(errorMessage);
  }
};

export const setLocale = (locale: string) => {
  if (i18nInstance.language !== locale && supportedLocales.includes(locale)) {
    return void i18nInstance.changeLanguage(locale);
  }
  return null;
};

export default i18nInstance;
